import React, { useCallback, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  Icon,
  Modal,
  SpaceBetween,
  Spinner,
  Tabs,
} from "@amzn/awsui-components-react";
import { OmsProduct, OmsReport } from "../../lusca-search-api/generated-src";
import useOmsReport from "../../hooks/useOmsReport";
import useOmsExport from "../../hooks/useOmsExport";
import useOmsFilterCriteria, {
  OmsFilterDraft,
} from "../../hooks/useOmsFilterCriteria";
import useOmsModalHeight from "../../hooks/useOmsModalHeight";
import OMSExportPanel, { OMSExportActions } from "./OMSExportPanel";
import OMSRecordFilters, { AppliedOmsFilters } from "./OMSRecordFilters";
import OMSExportSource from "./OMSExportSource";
import OMSReportSchedule from "./OMSReportSchedule";
import "./omsReportDownload.scss";

function ExportDialog({
  product,
  productLabel,
  report,
  availableReport,
  onClose,
  draft,
  onSaveDraft,
}: {
  product: OmsProduct;
  productLabel: string;
  report: OmsReport;
  availableReport: OmsReport;
  onClose: () => void;
  draft?: OmsFilterDraft;
  onSaveDraft: (draft: OmsFilterDraft) => void;
}) {
  const [tab, setTab] = useState(draft?.tab || "full");
  const [focusConfiguration, setFocusConfiguration] = useState(false);
  const full = useOmsExport(product, report, "full", availableReport);
  const filtered = useOmsExport(
    product,
    draft?.report || report,
    "filtered",
    availableReport,
    !draft?.editing
  );
  const criteria = useOmsFilterCriteria(
    filtered.sourceReport,
    filtered.job.data,
    draft
  );
  const current = tab === "full" ? full : filtered;
  const source = current.sourceReport;
  const filteredActive = Boolean(filtered.jobId) || filtered.create.isLoading;
  const acceptedFilters = filtered.job.data;
  const fullCsvFiles = full.job.data?.csvFiles || 1;
  const filteredCsvFiles = filtered.job.data?.csvFiles || 1;
  const preparing =
    current.create.isLoading ||
    (Boolean(current.jobId) &&
      !current.expired &&
      !current.unavailable &&
      !current.job.isError &&
      (!current.job.data || current.job.data.status === "PREPARING"));
  const { height, contentRef, titleRef, footerRef } = useOmsModalHeight();
  const close = useCallback(() => {
    onSaveDraft({
      snapshotId: filtered.sourceReport.snapshotId,
      report: filtered.sourceReport,
      editing: !filtered.jobId && !filtered.create.isLoading,
      criteria: criteria.criteria,
      operation: criteria.operation,
      tab,
    });
    onClose();
  }, [
    onSaveDraft,
    onClose,
    filtered.sourceReport,
    filtered.jobId,
    filtered.create.isLoading,
    criteria.criteria,
    criteria.operation,
    tab,
  ]);
  return (
    <Modal
      visible
      onDismiss={close}
      header={<span ref={titleRef}>{`Export ${productLabel} report`}</span>}
      size="x-large"
      position="top"
      height={height}
      closeAriaLabel="Close report export"
      footer={
        <div ref={footerRef} className="oms-report-download__footer">
          <Button variant={preparing ? "primary" : "normal"} onClick={close}>
            Close
          </Button>
          <OMSExportActions
            state={current}
            mode={tab === "full" ? "full" : "filtered"}
            filters={tab === "full" ? [] : criteria.filters}
            operation={tab === "full" ? "and" : criteria.operation}
            valid={tab === "full" || criteria.valid}
            onConfigure={
              tab === "filtered" ? () => setFocusConfiguration(true) : undefined
            }
          />
        </div>
      }
    >
      <div ref={contentRef}>
        <SpaceBetween size="l">
          <OMSExportSource
            report={source}
            availableReport={availableReport}
            accepted={Boolean(current.jobId) || current.create.isLoading}
            onUseLatest={() => {
              if (tab === "filtered") setFocusConfiguration(true);
              current.changeFilters();
            }}
          />
          {source.rowCount === 0 && !current.jobId && (
            <Alert type="info" header="Empty report">
              The export will contain headers only.
            </Alert>
          )}
          <Tabs
            ariaLabel="Export scope"
            activeTabId={tab}
            onChange={({ detail }) => setTab(detail.activeTabId)}
            tabs={[
              {
                id: "full",
                label: "Full report",
                content: (
                  <SpaceBetween size="m">
                    {!full.jobId && !full.create.isLoading && (
                      <Container
                        header={<Header variant="h3">Export contents</Header>}
                      >
                        <SpaceBetween size="s">
                          <Box>
                            Download all rows from the scheduled report.
                            Dashboard filters do not apply.
                          </Box>
                          <Box color="text-body-secondary">
                            One CSV with column headers, compressed in a ZIP.
                          </Box>
                        </SpaceBetween>
                      </Container>
                    )}
                    {fullCsvFiles > 1 && (
                      <Box color="text-body-secondary">
                        Prepared ZIP contains {fullCsvFiles.toLocaleString()}{" "}
                        CSV files. Create a new export for one CSV.
                      </Box>
                    )}
                    {(full.jobId || !full.create.isIdle) && (
                      <OMSExportPanel state={full} mode="full" />
                    )}
                  </SpaceBetween>
                ),
              },
              {
                id: "filtered",
                label: "Filtered export",
                content: (
                  <SpaceBetween size="m">
                    {(filteredActive || filtered.create.isError) && (
                      <OMSExportPanel state={filtered} mode="filtered" />
                    )}
                    {!filteredActive && (
                      <OMSRecordFilters
                        report={filtered.sourceReport}
                        state={criteria}
                        focusRequested={focusConfiguration}
                        onFocusHandled={() => setFocusConfiguration(false)}
                      />
                    )}
                    {filteredActive &&
                      (acceptedFilters || filtered.create.isLoading) && (
                        <AppliedOmsFilters
                          report={filtered.sourceReport}
                          filters={acceptedFilters?.filters || criteria.filters}
                          operation={
                            acceptedFilters?.operation || criteria.operation
                          }
                          textCaseSensitive={
                            acceptedFilters
                              ? acceptedFilters.textCaseSensitive
                              : true
                          }
                          compact={acceptedFilters?.status === "READY"}
                        />
                      )}
                    {filteredCsvFiles > 1 && (
                      <Box color="text-body-secondary">
                        Prepared ZIP contains{" "}
                        {filteredCsvFiles.toLocaleString()} CSV files. Create a
                        new export for one CSV.
                      </Box>
                    )}
                  </SpaceBetween>
                ),
              },
            ]}
          />
          {tab === "filtered" && !filteredActive && (
            <Box color="text-body-secondary">
              Matching rows are returned in one CSV with column headers,
              compressed in a ZIP.
            </Box>
          )}
        </SpaceBetween>
      </div>
    </Modal>
  );
}

function unavailableMessage(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 404) return "No completed scheduled report is available yet.";
  if (status === 403) return "You do not have access to download this report.";
  return "The report is temporarily unavailable. Refresh to try again.";
}

export default function OMSReportDownload({
  product,
}: {
  product: OmsProduct;
}) {
  const query = useOmsReport(product);
  const [snapshot, setSnapshot] = useState<OmsReport | null>(null);
  const [draft, setDraft] = useState<OmsFilterDraft>();
  const productLabel = {
    [OmsProduct.Channels]: "PV Channels",
    [OmsProduct.Addon]: "PV Addon",
    [OmsProduct.Tvod]: "PV TVOD",
  }[product];

  return (
    <>
      <section className="oms-report-card" aria-label="Scheduled report">
        <Container
          header={
            <Header
              variant="h2"
              description={
                <span className="oms-report-card__description">
                  Full or filtered CSV exports
                </span>
              }
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    iconName="refresh"
                    ariaLabel="Refresh scheduled report"
                    loading={query.isFetching}
                    loadingText="Refreshing report details"
                    wrapText={false}
                    onClick={() => {
                      query.refetch().catch(() => undefined);
                    }}
                  />
                  <Button
                    variant="primary"
                    iconName="download"
                    wrapText={false}
                    ariaHaspopup="dialog"
                    disabled={!query.data || query.isLoading || query.isError}
                    onClick={() => {
                      setSnapshot(query.data || null);
                    }}
                  >
                    Export report
                  </Button>
                </SpaceBetween>
              }
            >
              <span className="oms-report-card__title">
                <span className="oms-report-card__icon" aria-hidden="true">
                  <Icon name="file" size="medium" />
                </span>
                Scheduled report
              </span>
            </Header>
          }
        >
          <SpaceBetween size="xs">
            {query.isLoading && (
              <div className="oms-report-download__status" role="status">
                <Spinner />
                <span>Loading scheduled report…</span>
              </div>
            )}
            {query.isError && (
              <Alert
                type={
                  (query.error as { response?: { status?: number } })?.response
                    ?.status === 404
                    ? "info"
                    : "warning"
                }
                header="Report unavailable"
              >
                {unavailableMessage(query.error)}
              </Alert>
            )}
            {query.data && !query.isError && (
              <OMSReportSchedule
                product={product}
                sourceRefreshedAt={query.data.sourceRefreshedAt}
              />
            )}
            {query.isFetching && !query.isLoading && (
              <div role="status">
                <Box color="text-body-secondary">
                  Refreshing report details…
                </Box>
              </div>
            )}
          </SpaceBetween>
        </Container>
      </section>
      {snapshot && (
        <ExportDialog
          product={product}
          productLabel={productLabel}
          report={snapshot}
          availableReport={query.data || snapshot}
          draft={draft}
          onSaveDraft={setDraft}
          onClose={() => setSnapshot(null)}
        />
      )}
    </>
  );
}
